import { useState, useEffect } from 'react';
import type { Insight } from '@claude-code-insights/shared';
import { fetchReviewQueue, reviewInsight } from '../lib/api';

export function ReviewView() {
  const [items, setItems] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);

  async function loadQueue() {
    setLoading(true);
    try {
      const result = await fetchReviewQueue();
      setItems(result.items);
    } catch (err) {
      console.error('Failed to load review queue:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadQueue();
  }, []);

  async function handleAction(insightId: string, action: 'accept' | 'dismiss') {
    setProcessing(insightId);
    try {
      await reviewInsight(insightId, { action });
      setItems((prev) => prev.filter((item) => item.insightId !== insightId));
    } catch (err) {
      console.error('Review action failed:', err);
    } finally {
      setProcessing(null);
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">Review Queue</h2>
        <span className="text-sm text-obsidian-text-muted">
          {items.length} item{items.length !== 1 ? 's' : ''} pending
        </span>
      </div>

      {loading ? (
        <div className="py-8 text-center text-obsidian-text-muted">Loading...</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-obsidian-text-muted text-sm">Review queue is empty</p>
          <p className="text-obsidian-text-faint text-xs mt-2">
            Items with relevance score 3-4 will appear here for triage
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.insightId} className="card">
              <div className="flex items-start gap-3">
                {/* Relevance */}
                <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold bg-obsidian-warning/20 text-obsidian-warning">
                  {item.relevanceScore}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium">{item.title}</h3>
                  <p className="text-xs text-obsidian-text-muted mt-1">
                    {item.summary}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="badge bg-obsidian-bg-tertiary text-obsidian-text-faint text-xs">
                      {item.category}
                    </span>
                    <span className="text-xs text-obsidian-text-faint">
                      {item.sourceType} · {item.sourceAuthor}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAction(item.insightId, 'accept')}
                    disabled={processing === item.insightId}
                    className="px-3 py-1.5 text-sm bg-obsidian-success/20 text-obsidian-success rounded-lg hover:bg-obsidian-success/30 transition-colors disabled:opacity-50"
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => handleAction(item.insightId, 'dismiss')}
                    disabled={processing === item.insightId}
                    className="px-3 py-1.5 text-sm bg-obsidian-error/20 text-obsidian-error rounded-lg hover:bg-obsidian-error/30 transition-colors disabled:opacity-50"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
